import { useState } from 'react';
import { Activity, Flame } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { formatMinutes } from '@/lib/activityQueries';
import { niceCeiling, readableSeconds } from './hoursScale';
import { niceCount } from './countScale';
import { cn } from '@/lib/utils';
import type { TrendsData } from './DashboardTrends';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * IS THE PLATFORM BEING USED?
 *
 * Measured learning time across the whole organisation, month by month — the
 * same `learning_days.seconds` an employee's own activity chart is drawn from,
 * summed over everybody instead of one person.
 *
 * ── Why not enrolments and completions ────────────────────────────────────
 * This panel counted those two, and they are counts of decisions an
 * ADMINISTRATOR made rather than of anything anybody did: assign a course to
 * 200 people and the chart reads 200 with nobody having opened it. In practice
 * it drew as a flat floor with one vertical spike on the month somebody ran a
 * bulk assignment — which says nothing about usage and looks like a fault.
 *
 * Time can only move when the platform is genuinely used, so that is what it
 * shows now.
 *
 * ── Two measures, one at a time ───────────────────────────────────────────
 *
 * Hours and people are the two halves of "is this being used", and neither
 * answers it alone: ten hours is one person who watched everything or forty
 * who watched a little, and those are opposite situations wearing the same
 * total. People were buried in the hover, where you had to already suspect the
 * problem in order to go looking for it.
 *
 * The control switches which one the line draws. NOT two lines on two y-axes
 * — an axis of hours and an axis of headcount share no units, and a chart
 * where the crossing point means nothing invites a reader to interpret it.
 * Same x-axis, same shape of question, one measure at a time.
 *
 * ── Fixed viewBox, stretched by CSS ───────────────────────────────────────
 * `preserveAspectRatio="none"` lets one set of coordinates fill any width, so
 * the geometry never has to know how wide the card is. The cost is that
 * strokes stretch with it, which is why each is given `non-scaling-stroke` and
 * stays 2px at any size — and why every axis label is HTML around the SVG
 * rather than `<text>` inside it.
 * ═══════════════════════════════════════════════════════════════════════════
 */

type Month = TrendsData['usage']['months'][number];

type Measure = 'time' | 'people';

/**
 * What each measure is, and how to say it.
 *
 * There is no total here, and there must not be one added back for `people`.
 * Hours across six months add up; PEOPLE do not — the same person learning in
 * March and in April is one person, and summing the monthly counts reports
 * them as two. `peakNote` describes a SINGLE month, which is the one summary
 * that stays honest for both measures.
 */
const MEASURE: Record<
  Measure,
  {
    option: string;
    of: (m: Month) => number;
    ceiling: (peak: number) => number;
    axis: (value: number) => string;
    peakNote: (m: Month) => string;
  }
> = {
  time: {
    option: 'Learning time',
    of: (m) => m.seconds,
    ceiling: niceCeiling,
    axis: readableSeconds,
    peakNote: (m) => readableSeconds(m.seconds),
  },
  people: {
    option: 'People active',
    of: (m) => m.learners,
    ceiling: niceCount,
    axis: (value) => String(Math.round(value)),
    peakNote: (m) => `${m.learners} ${m.learners === 1 ? 'person' : 'people'}`,
  },
};

/** `2026-09` → `Sep`. The server sends the key; the client owns the language. */
function monthLabel(key: string): string {
  const [year, month] = key.split('-').map(Number);
  if (!year || !month) return key;
  return new Date(year, month - 1, 1).toLocaleDateString('en-IN', { month: 'short' });
}

/** `2026-09` → `September 2026`, for the hover and the screen reader. */
function longMonthLabel(key: string): string {
  const [year, month] = key.split('-').map(Number);
  if (!year || !month) return key;
  return new Date(year, month - 1, 1).toLocaleDateString('en-IN', {
    month: 'long',
    year: 'numeric',
  });
}

/** One bar: what it is worth, and how much of the axis it fills. */
export interface Bar {
  key: string;
  label: string;
  value: number;
  /** 0–100, the share of the axis height this bar occupies. */
  percent: number;
  /** The month we are in — drawn a shade apart from the finished ones. */
  isCurrent: boolean;
}

/**
 * The six bars, from the months and whichever measure is selected.
 *
 * `peak` is the AXIS ceiling rather than the largest value, so the tallest bar
 * stops short of the top — see `niceCeiling`. Passing the raw maximum here
 * would put one bar flush against the ceiling every time, which reads as
 * clipped rather than as the highest.
 */
export function barsFor(
  months: Month[],
  peak: number,
  of: (m: Month) => number = (m) => m.seconds,
): Bar[] {
  return months.map((m, i) => {
    const value = of(m);

    return {
      key: m.month,
      label: monthLabel(m.month),
      value,
      /* Guarded: `peak` is never 0 in practice because `niceCeiling` has a
         floor, but a division that can produce NaN renders as a bar with no
         height and no error — the silent kind of wrong. */
      percent: peak <= 0 ? 0 : Math.max(0, Math.min(100, (value / peak) * 100)),
      isCurrent: i === months.length - 1,
    };
  });
}

/** A point on the line, in PERCENT of the plot box: x across, y down. */
export interface Point {
  x: number;
  y: number;
}

/**
 * The line's points, as percentages of the plot rather than SVG units.
 *
 * Percent so the dots can be plain HTML positioned over the chart. The path
 * itself is an SVG stretched with `preserveAspectRatio="none"`, which is fine
 * for a stroke carrying `non-scaling-stroke` and ruinous for a circle — it
 * turns every marker into an ellipse, which is what the previous version of
 * this chart did.
 *
 * A single month sits in the middle rather than at x=0: the natural spacing
 * divides by `length - 1`, which is a division by zero on one point and
 * renders as `NaN` in the path, drawing nothing at all.
 */
export function pointsFor(bars: Bar[]): Point[] {
  return bars.map((bar, i) => ({
    x: bars.length > 1 ? (i / (bars.length - 1)) * 100 : 50,
    y: 100 - bar.percent,
  }));
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * THE TREND LINE
 *
 * A least-squares fit through the six months, drawn as one straight line from
 * edge to edge.
 *
 * It exists because the series itself is spiky — three near-zero months and
 * then 687 hours — and a line joining those points reads as a climb followed
 * by a collapse when what actually happened is one exceptional month. The fit
 * answers the different question: setting aside which month was busy, is this
 * going up or down.
 *
 * Fitted on the PERCENTAGES rather than the raw values, which is the same fit
 * — scaling y by a constant scales the slope by that constant and moves
 * nothing — and saves converting back to draw it.
 *
 * Endpoints are clamped into the plot. A regression can extrapolate outside
 * the range of its own data, and a trend line leaving the top of the chart
 * looks like a rendering fault rather than like a steep slope. Clamping
 * flattens such a line slightly; it never reverses its direction.
 */
export function trendFor(bars: Bar[]): { from: number; to: number } | null {
  /* One point is a value, not a trend, and two points ARE the line already —
     a fit through them adds nothing and draws directly over the series. */
  if (bars.length < 3) return null;

  const n = bars.length;
  const meanX = (n - 1) / 2;
  const meanY = bars.reduce((sum, b) => sum + b.percent, 0) / n;

  let covariance = 0;
  let variance = 0;
  bars.forEach((bar, i) => {
    covariance += (i - meanX) * (bar.percent - meanY);
    variance += (i - meanX) ** 2;
  });

  /* Unreachable with three or more distinct x values, and guarded because a
     NaN here draws a path attribute of "MNaN" — no line, no error. */
  if (variance === 0) return null;

  const slope = covariance / variance;
  const intercept = meanY - slope * meanX;
  const clamp = (v: number) => Math.max(0, Math.min(100, v));

  return {
    /* y is measured DOWN from the top of the box, so a rising trend has a
       smaller `to` than `from`. The caller never has to know that. */
    from: 100 - clamp(intercept),
    to: 100 - clamp(intercept + slope * (n - 1)),
  };
}

export function PlatformActivity({ usage }: { usage: TrendsData['usage'] }) {
  const months = usage.months;
  const [measure, setMeasure] = useState<Measure>('time');
  const [hover, setHover] = useState<number | null>(null);

  const spec = MEASURE[measure];

  /* Never zero: a month with no learning still draws an axis and a flat line
     along the floor rather than dividing by zero. */
  const peak = spec.ceiling(Math.max(0, ...months.map(spec.of)));
  const bars = barsFor(months, peak, spec.of);
  const points = pointsFor(bars);
  const trend = trendFor(bars);
  const LINE = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(2)} ${p.y.toFixed(2)}`).join(' ');

  const gridlines = [1, 0.5, 0];
  const active = hover !== null ? months[hover] : null;

  /** The largest month — now the only summary above the plot. */
  const busiestIndex = months.reduce(
    (best, m, i) => (best === -1 || spec.of(m) > spec.of(months[best]!) ? i : best),
    -1,
  );
  const busiest =
    busiestIndex === -1 || spec.of(months[busiestIndex]!) === 0 ? null : months[busiestIndex]!;

  /**
   * Arrow keys walk the months.
   *
   * The hover was built entirely out of `onMouseEnter`, which makes the only
   * way to read a figure on this chart a mouse and a steady hand. The same
   * state drives both, so this is a second way in rather than a second
   * behaviour to keep in step.
   */
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (months.length === 0) return;

    /* The first arrow key lands on the latest month rather than stepping off
       nothing — it is the one a reader arriving at this chart wants. */
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      setHover(hover === null ? months.length - 1 : Math.min(months.length - 1, hover + 1));
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      setHover(hover === null ? months.length - 1 : Math.max(0, hover - 1));
    } else if (e.key === 'Home') {
      e.preventDefault();
      setHover(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      setHover(months.length - 1);
    } else if (e.key === 'Escape') {
      setHover(null);
    }
  };

  return (
    /*
      ── THE CARD SIZES TO ITS CONTENT ─────────────────────────────────────

      `h-full` made this card as tall as whatever sat beside it in the grid,
      and `flex-1` on the plot below meant the CHART absorbed every pixel of
      that. Six monthly points do not need four hundred pixels of height —
      the shape of the line is legible in a third of it, and the rest was a
      panel pushing everything under it off the screen.

      Without `h-full` the card ends where its content ends, and the plot is
      given a height rather than taking one.
    */
    <Card className="flex flex-col overflow-hidden">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b px-4 py-3">
        <Activity className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        <h2 className="text-sm font-semibold">Platform activity</h2>
        <p className="text-xs text-muted-foreground">
          Across the organisation · last {months.length} months
        </p>

        {/* The same plain select the hours card uses for its range — one
            styled dropdown here would be a second pattern for one job. */}
        <select
          value={measure}
          onChange={(e) => {
            setMeasure(e.target.value as Measure);
            /* The hovered month stays; the figure it describes does not. */
            setHover(null);
          }}
          aria-label="Measure"
          className="ml-auto rounded-lg border bg-background px-2.5 py-1 text-xs font-medium"
        >
          <option value="time">{MEASURE.time.option}</option>
          <option value="people">{MEASURE.people.option}</option>
        </select>
      </div>

      {months.length === 0 ? (
        <p className="px-5 py-12 text-center text-sm text-muted-foreground">
          Nothing has been recorded yet.
        </p>
      ) : (
        <div className="flex flex-col px-4 py-3">
          {/*
            ── The peak, and no total ────────────────────────────────────────

            This carried a running total beside the peak until it was dropped
            by request. Losing it costs nothing the chart does not already
            say: the plot IS the six months, and a total could never
            distinguish a steady half-year from one busy week anyway — five
            months at zero and one at ten hours read "10.3h in total", which
            is true and tells nobody where the ten hours went.

            The peak is the fact that survives, because it is the one the
            shape cannot state precisely: the line shows July is tallest, and
            this says by how much.
          */}
          <div className="mb-2.5 flex flex-wrap items-baseline gap-x-3 gap-y-1">
            {busiest && (
              <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                <Flame className="h-3.5 w-3.5 shrink-0 text-muted-foreground/70" aria-hidden />
                busiest <span className="font-medium text-foreground">
                  {longMonthLabel(busiest.month)}
                </span>
                {', '}
                <span className="tabular-nums">{spec.peakNote(busiest)}</span>
              </span>
            )}
          </div>

          {/*
            ── BARS, NOT A LINE ──────────────────────────────────────────────

            A line says the value moved CONTINUOUSLY between its points, and
            for six monthly totals there is no such thing: nothing exists
            between June and July, so the segment joining them drew a quantity
            that was never measured. On this data it was actively misleading —
            three near-zero months, then 687 hours in July — and the line
            turned a spike into a climb, with the area fill under it
            exaggerating the slope.

            Six discrete buckets compared by magnitude is the plain case for
            bars. Each one starts at a shared baseline, so two months are
            compared by length rather than by reading a gradient, and a quiet
            month is visibly a short bar rather than a point on a slope.

            It is also the same chart the Organisation hours activity card draws, in the
            same HTML, which is one pattern on the dashboard instead of two —
            and it drops the stretched `viewBox` this used, whose
            `preserveAspectRatio="none"` was quietly turning every point
            marker into an ellipse.
          */}
          {/* 6.5rem plus 1.5cm — 1cm is 37.8px at CSS's 96dpi, so 1.5cm is
              about 3.5rem. */}
          <div className="flex h-[10rem] gap-2">
            <div className="relative w-10 shrink-0 text-[10px] tabular-nums text-muted-foreground">
              {gridlines.map((fraction) => (
                <span
                  key={fraction}
                  className="absolute right-0 -translate-y-1/2"
                  style={{ top: `${(1 - fraction) * 100}%` }}
                >
                  {spec.axis(peak * fraction)}
                </span>
              ))}
            </div>

            <div
              className="relative min-w-0 flex-1 rounded-md outline-none ring-offset-2 focus-visible:ring-2 focus-visible:ring-ring"
              tabIndex={0}
              role="application"
              aria-label={`${spec.option} across the organisation over the last ${months.length} months. Use the arrow keys to read each month.`}
              onKeyDown={onKeyDown}
              onBlur={() => setHover(null)}
            >
              {/* Recessive: the data is the thing, the grid is scaffolding. */}
              {gridlines.map((fraction) => (
                <span
                  key={fraction}
                  className="pointer-events-none absolute inset-x-0 border-t border-border"
                  style={{ top: `${(1 - fraction) * 100}%`, opacity: fraction === 0 ? 0.9 : 0.4 }}
                  aria-hidden
                />
              ))}

              {/*
                The series and its fill, stretched to whatever width the card
                has. `non-scaling-stroke` keeps every stroke 2px however far
                the box is stretched; the MARKERS are HTML below, because a
                circle in a stretched viewBox is an ellipse.
              */}
              <svg
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
                className="absolute inset-0 h-full w-full text-brand-700 dark:text-brand-300"
                aria-hidden
              >
                {points.length > 0 && (
                  <>
                    <path
                      d={`${LINE} L${points[points.length - 1]!.x.toFixed(2)} 100 L${points[0]!.x.toFixed(2)} 100 Z`}
                      fill="currentColor"
                      opacity={0.1}
                    />
                    <path
                      d={LINE}
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      strokeLinejoin="round"
                      strokeLinecap="round"
                      vectorEffect="non-scaling-stroke"
                    />
                  </>
                )}

                {/*
                  ── THE TREND, DASHED AND UNDERSTATED ────────────────────────

                  Dashed and thinner than the series on purpose: one of these
                  two lines is measured and the other is inferred, and drawing
                  them alike would invite a reader to treat the fit as data.
                */}
                {trend && (
                  <line
                    x1={0}
                    y1={trend.from}
                    x2={100}
                    y2={trend.to}
                    className="stroke-foreground"
                    strokeWidth={1.5}
                    strokeDasharray="4 3"
                    strokeLinecap="round"
                    vectorEffect="non-scaling-stroke"
                    opacity={0.45}
                  />
                )}
              </svg>

              {/* The markers, as HTML, so they stay round. */}
              {points.map((point, i) => (
                <span
                  key={bars[i]!.key}
                  className={cn(
                    'pointer-events-none absolute -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-card bg-brand-700 transition-[width,height] dark:bg-brand-300',
                    hover === i ? 'h-[11px] w-[11px]' : 'h-2 w-2',
                  )}
                  style={{ left: `${point.x}%`, top: `${point.y}%` }}
                  aria-hidden
                />
              ))}

              {/*
                A column per month rather than a target per point: the reader
                is pointing at a month, and asking them to hit an 8px circle to
                find out about it is a chart that only works with a mouse and a
                steady hand.
              */}
              <div className="absolute inset-0 flex">
                {months.map((month, i) => (
                  <button
                    key={month.month}
                    type="button"
                    tabIndex={-1}
                    aria-hidden
                    className="h-full flex-1 cursor-default"
                    onMouseEnter={() => setHover(i)}
                    onMouseLeave={() => setHover(null)}
                  />
                ))}
              </div>

              {hover !== null && points[hover] && (
                <span
                  className="pointer-events-none absolute inset-y-0 w-px bg-foreground/30"
                  style={{ left: `${points[hover]!.x}%` }}
                  aria-hidden
                />
              )}

              {active && hover !== null && (
                <div
                  className={cn(
                    'pointer-events-none absolute bottom-full z-10 mb-1 w-max rounded-md border bg-popover px-2.5 py-1.5',
                    'text-xs shadow-md',
                    // Flips side near the right edge so it is never clipped.
                    /* Flips side past the midpoint so it is never clipped —
                       and the first and last points sit ON the edges now, so
                       the untranslated case needs its own nudge inward. */
                    hover > months.length / 2 ? '-translate-x-full' : 'translate-x-0',
                  )}
                  style={{ left: `${points[hover]!.x}%` }}
                >
                  <p className="font-medium">{longMonthLabel(active.month)}</p>
                  {/*
                    BOTH measures, whichever is drawn. The bars answer the
                    question you picked; the hover is where the other half
                    lives, because "ten hours" and "one person" is a different
                    finding from "ten hours" and "forty people".
                  */}
                  <p className="text-muted-foreground">
                    <span className="font-medium tabular-nums text-foreground">
                      {formatMinutes(Math.round(active.seconds / 60))}
                    </span>{' '}
                    of learning
                  </p>
                  <p className="text-muted-foreground">
                    <span className="font-medium tabular-nums text-foreground">
                      {active.learners}
                    </span>{' '}
                    {active.learners === 1 ? 'person' : 'people'} active
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* The month axis, also HTML. */}
          <div className="mt-2 flex pl-12 text-[10px] text-muted-foreground">
            {months.map((m, i) => (
              <span
                key={m.month}
                className={cn(
                  'flex-1',
                  i === 0 ? 'text-left' : i === months.length - 1 ? 'text-right' : 'text-center',
                  hover === i && 'font-medium text-foreground',
                )}
              >
                {monthLabel(m.month)}
              </span>
            ))}
          </div>

          {/*
            The figures in text, for a screen reader and for anybody who wants
            the numbers rather than the shape. A chart that exists only as
            geometry is unreadable to half the ways people read a page.
          */}
          <table className="sr-only">
            <caption>{spec.option} across the organisation, by month</caption>
            <thead>
              <tr>
                <th scope="col">Month</th>
                <th scope="col">Learning time</th>
                <th scope="col">People active</th>
              </tr>
            </thead>
            <tbody>
              {months.map((m) => (
                <tr key={m.month}>
                  <th scope="row">{longMonthLabel(m.month)}</th>
                  <td>{formatMinutes(Math.round(m.seconds / 60))}</td>
                  <td>{m.learners}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
